import React from "react";
import {
  RiFlashlightLine, RiArrowRightLine } from "react-icons/ri";
import {
  type IntervalGasViewModel,
  type CustomerStockStatus,
  MOCK_FIELD_API_NAMES,
  toMonthDay,
  formatDepletionYearHint,
  dateToPct,
  getTodayIso,
} from "./intervalGasView.model";
import { type IntervalRangeFontKey, intervalRangeFontStyle } from "./intervalRangeFont";

/* 狀態文字色（白底對話框上使用各狀態的深色）；export 供鋼瓶上的狀態氣泡共用 */
export const STATUS_LABEL_COLOR: Record<CustomerStockStatus, string> = {
  充足: "#0F6E56",
  留意: "#92400E",
  偏低: "#9A3412",
  建議補充: "#991B1B",
};

/* 時間軸區間色帶：正常為白色，留意用黃色升溫（偏低/建議補充背景已換色，色帶維持白色） */
const STATUS_BAND: Record<CustomerStockStatus, string> = {
  充足: "rgba(255,255,255,0.95)",
  留意: "#FDE047",
  偏低: "rgba(255,255,255,0.95)",
  建議補充: "rgba(255,255,255,0.95)",
};

/** 小標籤：標示此欄位為前端估算。onDark = 用於彩色 Hero 背景，onLight = 用於白卡 */
export const MockFieldTag = ({ title, tone = "onLight" }: { title?: string; tone?: "onLight" | "onDark" }) => {
  const toneStyle =
    tone === "onDark"
      // 原本是 #CDE7CE 字配 #69BFA0 底，對比約 1.8:1——在彩色 hero 上等同白字，讀不到。
      // 改成深墨綠字配淡綠底，對比拉到 7:1 以上，同時仍與周圍的白字系有區別。
      ? { background: "rgba(255,255,255,0.92)", color: "#1d6b52", border: "none" }
      : { background: "rgba(252,211,77,0.3)", color: "#92400E", border: "0.5px solid rgba(252,211,77,0.4)" };

  return (
    <span
      title={title}
      style={{
        fontSize: 10,
        fontWeight: 700,
        padding: "2px 6px",
        borderRadius: 6,
        lineHeight: 1.2,
        flexShrink: 0,
        ...toneStyle,
      }}>
      估算
    </span>
  );
};

// ─── 建議配送文字（區間日期正下方）────────────────────────────────────────

const DispatchHintLine = ({ vm }: { vm: IntervalGasViewModel }) => {
  if (!vm.dispatchByDate || !vm.dispatchHint) return null;

  return (
    <div
      className="dispatch-hint-line"
      style={{
        fontSize: 16,
        fontWeight: 500,
        color: "rgba(255,255,255,0.88)",
        lineHeight: 1.35,
        marginTop: 8,
        display: "flex",
        alignItems: "center",
        gap: 6,
        whiteSpace: "nowrap",
      }}>
      <span>{vm.dispatchHint}</span>
      {vm.dispatchHintIsMock && <MockFieldTag tone="onDark" title={MOCK_FIELD_API_NAMES.dispatch_lower_date} />}
    </div>
  );
};

// ─── 時間軸：今天（白點）→ 預估用完窗（色帶）────────────────────────────────

const TRACK = { left: 4, right: 96 };

const toTrackPct = (date: string, start: string, end: string): number => {
  const raw = dateToPct(date, start, end);
  return TRACK.left + (raw / 100) * (TRACK.right - TRACK.left);
};

const todayLabelTransform = (pct: number): string => {
  if (pct < 22) return "translateX(0)";
  if (pct > 78) return "translateX(-100%)";
  return "translateX(-50%)";
};


const IntervalTimeline = ({ vm }: { vm: IntervalGasViewModel }) => {
  const start = vm.timelineRangeStart;
  const end = vm.timelineRangeEnd;
  const todayPct = toTrackPct(getTodayIso(), start, end);
  const bandLeft = toTrackPct(vm.depletionLow!, start, end);
  const bandWidth = Math.max(4, toTrackPct(vm.depletionHigh!, start, end) - bandLeft);

  return (
    <div style={{ marginTop: 20 }}>
      {/* 今天日期：對齊今天白點 */}
      <div style={{ position: "relative", height: 32, marginBottom: 4 }}>
        <span
          style={{
            position: "absolute",
            left: `${todayPct}%`,
            transform: todayLabelTransform(todayPct),
            fontSize: 11,
            fontWeight: 600,
            color: "rgba(255,255,255,0.82)",
            lineHeight: 1.25,
            textAlign: todayPct < 22 ? "left" : todayPct > 78 ? "right" : "center",
            whiteSpace: "nowrap",
          }}>
          今天
          <br />
          <span style={{ fontVariantNumeric: "tabular-nums" }}>{toMonthDay(getTodayIso())}</span>
        </span>
      </div>

      <div style={{ position: "relative", height: 6, borderRadius: 999, background: "rgba(255,255,255,0.22)" }}>
        {/* 預估用完區間色帶（虛線邊：標示為預估區間） */}
        <div
          style={{
            position: "absolute",
            left: `${bandLeft}%`,
            width: `${bandWidth}%`,
            top: -1,
            height: 8,
            borderRadius: 999,
            background: STATUS_BAND[vm.stockStatus],
            outline: "1px dashed rgba(255,255,255,0.65)",
            outlineOffset: 1,
          }}
        />
        {/* 今天（實心白點） */}
        <div
          style={{
            position: "absolute",
            left: `${todayPct}%`,
            top: "50%",
            transform: "translate(-50%, -50%)",
            width: 12,
            height: 12,
            borderRadius: "50%",
            background: "#fff",
            boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
            zIndex: 2,
          }}
        />
      </div>

      {/* 底部標籤：上次叫桶、預估用完 */}
      <div style={{ position: "relative", height: 34, marginTop: 6 }}>
        <span
          style={{
            position: "absolute",
            left: 0,
            fontSize: 11,
            color: "rgba(255,255,255,0.55)",
            lineHeight: 1.35,
            textAlign: "left",
          }}>
          上次叫桶
          <br />
          <span style={{ color: "rgba(255,255,255,0.82)", fontWeight: 600 }}>{toMonthDay(vm.lastOrderDate)}</span>
        </span>

        <span
          style={{
            position: "absolute",
            right: 0,
            textAlign: "right",
            fontSize: 11,
            color: "rgba(255,255,255,0.55)",
            lineHeight: 1.35,
          }}>
          預估用完
          <br />
          <span style={{ color: "rgba(255,255,255,0.82)", fontWeight: 600 }}>{toMonthDay(vm.depletionHigh)}</span>
        </span>
      </div>
    </div>
  );
};

// ─── Hero 內容：預估用完區間（主）＋ 一句情境 ＋ 時間軸 ──────────────────────
// 狀態 pill 在時間軸「今天」錨點上方；背景色與瓶內水位輔助傳達狀態

export const GasPredictionHeroContent = ({
  vm,
  rangeFont = "default",
}: {
  vm: IntervalGasViewModel;
  rangeFont?: IntervalRangeFontKey;
}) => {
  const [rangeA, rangeB] = vm.depletionRangeLabel.split("~").map((s) => s.trim());
  const yearHint = formatDepletionYearHint(vm.depletionLow, vm.depletionHigh);
  const longLabel = vm.depletionRangeLabel.length > 15;
  const baseFont = longLabel ? 38 : 52;
  const font = intervalRangeFontStyle(rangeFont);
  const bigFont = Math.round(baseFont * font.fontSizeScale);

  const lastOrderLabel = toMonthDay(vm.lastOrderDate);
  const daysSinceLastOrder = (() => {
    if (!vm.lastOrderDate) return null;
    const a = new Date(`${vm.lastOrderDate}T00:00:00+08:00`).getTime();
    const b = new Date(`${getTodayIso()}T00:00:00+08:00`).getTime();
    if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
    return Math.max(0, Math.round((b - a) / 86400000));
  })();

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 8,
          marginTop: 6,
          flexWrap: "wrap",
        }}>
        {yearHint ? (
          <span
            style={{
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: 0.4,
              color: "rgba(255,255,255,0.72)",
              fontVariantNumeric: "tabular-nums",
            }}>
            {yearHint}
          </span>
        ) : null}
        {vm.lastOrderDate && lastOrderLabel !== "--" ? (
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: 0.2,
              color: "rgba(255,255,255,0.72)",
              fontVariantNumeric: "tabular-nums",
            }}>
            · 上次叫桶 {lastOrderLabel} 
            {daysSinceLastOrder != null && daysSinceLastOrder > 0 ? (
              <span style={{ fontWeight: 500, color: "rgba(255,255,255,0.55)" }}>
                {`（已過 ${daysSinceLastOrder} 天）`}
              </span>
            ) : null}
          </span>
        ) : null}
      </div>

      <div
        className="depletion-range-display"
        style={{
          fontSize: `clamp(24px, ${longLabel ? 8.2 : 11}vw, ${bigFont}px)`,
          fontWeight: font.fontWeight,
          fontFamily: font.fontFamily,
          letterSpacing: font.letterSpacing,
          lineHeight: 1.02,
          color: "#fff",
          marginTop: 4,
          fontVariantNumeric: "tabular-nums",
          textShadow: rangeFont !== "default" ? "0 8px 22px rgba(0,40,90,0.22)" : undefined,
        }}>
        <span className="depletion-range-part">{rangeA}</span>
        {rangeB && (
          <>
            <span className="depletion-range-sep" style={{ fontWeight: 500, color: "rgba(255,255,255,0.42)", padding: "0 3px" }}>
              ~
            </span>
            <span className="depletion-range-part">{rangeB}</span>
          </>
        )}
      </div>

      <DispatchHintLine vm={vm} />

      <IntervalTimeline vm={vm} />
    </>
  );
};

// ─── 叫桶 CTA（合併原「立即預約叫桶」按鈕與商業提醒卡）──────────────────────
//   提醒日前  → 品牌藍「立即預約叫桶」＋「X 會提醒您」
//   已到提醒日（留意／偏低常見）→ 琥珀「建議先預約」＋配送建議
//   進入預估區間 → 紅色「建議立即預約」＋緊急說明

const CTA_TONE_STYLE: Record<
  "default" | "strong" | "urgent",
  { cls: string }
> = {
  /* 色值全在 all.scss 的 .cta-pill--* 裡（實色 + 同色接觸影兩個變數）。
     這裡只決定用哪一組。 */
  default: { cls: "cta-pill--blue" },
  strong: { cls: "cta-pill--amber" },
  urgent: { cls: "cta-pill--red" },
};

export const OrderCtaButton = ({
  vm,
  onClick,
}: {
  vm: IntervalGasViewModel;
  onClick: () => void;
}) => {
  if (vm.showAutoDispatchMessage) return null;

  const r = vm.activeReminder;
  const useReminderTitle = r && (r.tone === "strong" || r.tone === "urgent");
  const title = useReminderTitle ? r.title : "立即預約叫桶";
  /* CTA 的顏色原本只看「提醒」的 tone，於是會出現 hero 已經是紅色（建議補充）、
     底下的按鈕還是品牌藍的狀況——同一個畫面對同一件事講了兩種急迫度。
     改成取「提醒 tone」與「庫存狀態」兩者中較急的一個：狀態只會往上升級，不會把
     已經是紅色的提醒降回藍色。 */
  const RANK = { default: 0, strong: 1, urgent: 2 } as const;
  const reminderTone = r?.tone === "strong" ? "strong" : r?.tone === "urgent" ? "urgent" : "default";
  const statusTone =
    vm.stockStatus === "建議補充" ? "urgent" : vm.stockStatus === "偏低" || vm.stockStatus === "留意" ? "strong" : "default";
  const toneKey = RANK[statusTone] > RANK[reminderTone] ? statusTone : reminderTone;
  const tone = CTA_TONE_STYLE[toneKey];

  return (
    <div>
      <button
        type="button"
        onClick={onClick}
        className={`cta-pill cta-pill--go ${tone.cls}`}
        style={{ padding: "17px 22px" }}>
        {/* 左側佔位與右側箭頭等寬，標籤才會真的置中 */}
        <span className="cta-pill-spacer" aria-hidden="true" />
        {/* 標題可能被提醒換成較長的一句（如「該叫瓦斯了」），所以不設 nowrap，讓它自己折行置中 */}
        <span className="cta-pill-label" style={{ fontSize: 18 }}>{title}</span>
        <RiArrowRightLine className="cta-pill-go-icon" size={22} color="#fff" aria-hidden="true" />
      </button>
    </div>
  );
};

// ─── 自動安排通知（notify_tier=auto 時取代 CTA）──────────────────────────────

export const ReminderStageBlock = ({ vm }: { vm: IntervalGasViewModel }) => {
  if (vm.showAutoDispatchMessage) {
    return (
      <div
        style={{
          background: "#fff",
          borderRadius: 16,
          padding: "13px 15px",
          boxShadow: "0 3px 10px -8px rgba(31,41,55,0.18)",
          display: "flex",
          gap: 11,
          alignItems: "center",
        }}>
        <span
          style={{
            width: 38,
            height: 38,
            borderRadius: 11,
            background: "#e1f5ee",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}>
          <RiFlashlightLine size={17} color="#1d9e75" />
        </span>
        <span style={{ minWidth: 0 }}>
          <span style={{ display: "block", fontSize: 14, fontWeight: 700, color: "#1f2937" }}>自動安排中</span>
          <span style={{ display: "block", fontSize: 12, color: "#6B7280", marginTop: 2 }}>
            系統將依用量預測主動為您安排配送，無需手動叫桶
          </span>
        </span>
      </div>
    );
  }

  return null;
};
